const API_BASE =
  process.env.REACT_APP_DISCOVERY_API_URL ||
  "https://api.spinninbeans.com";

const DEFAULT_TRACKS_PER_CRATE = 6;

export const discoverSourceOptions = [
  {
    id: "sb-vibe",
    label: "SB-VIBE",
    shortLabel: "Vibe",
    description: "Fresh pulls from the Spinnin' Beans taste lane.",
  },
  {
    id: "staff-pick",
    label: "Staff Pick",
    shortLabel: "Staff",
    description: "Hand-pulled crates from the Spinnin' Beans crew.",
  },
  {
    id: "guest",
    label: "Guest",
    shortLabel: "Guest",
    description: "Rotating DJ and tastemaker mini-crates.",
  },
  {
    id: "user-submission",
    label: "User Submission",
    shortLabel: "User",
    description: "Community-submitted POVs and small crates.",
  },
];

const miniCrateSections = [
  {
    id: "2026-04-27-morning-afterglow",
    date: "2026-04-27",
    dayLabel: "Monday",
    title: "Morning Afterglow",
    theme: "sunlit dusty house",
    summary: "Loose, warm, and a little blurry after the weekend.",
    sortOrder: 10,
  },
  {
    id: "2026-04-26-warehouse-notes",
    date: "2026-04-26",
    dayLabel: "Sunday",
    title: "Warehouse Notes",
    theme: "leftfield club pressure",
    summary: "Oddball rollers, side-room pressure, and late checkout percussion.",
    sortOrder: 20,
  },
  {
    id: "2026-04-25-guest-window",
    date: "2026-04-25",
    dayLabel: "Saturday",
    title: "Guest Window",
    theme: "open decks from the friends table",
    summary: "Small stacks from guests and community clips worth replaying.",
    sortOrder: 30,
  },
];

const miniCrateFixtures = [
  {
    id: "crate-sun-through-curtains",
    slug: "sun-through-the-curtains",
    sectionId: "2026-04-27-morning-afterglow",
    source: "sb-vibe",
    title: "Sun Through the Curtains",
    subtitle: "Daylight house for slow starts",
    note: "A soft landing strip: brushed drums, warm chords, and one low-key garage lean near the end.",
    publishedAt: "2026-04-27T09:15:00-06:00",
    curator: {
      id: "sb-vibe",
      name: "SB-VIBE",
      role: "Taste engine",
      handle: "@spinninbeans",
    },
    artwork: {
      style: "gradient-paper",
      alt: "Washed yellow paper texture with a taped mini-crate label.",
      palette: {
        background: "#f6d778",
        foreground: "#211f1a",
        accent: "#377f73",
      },
    },
    tags: ["daylight-dust", "deep-house", "warm-up", "dusty"],
    tracks: [
      {
        id: "track-amber-decaf",
        title: "Amber Decaf",
        artist: "Talia North",
        musicalKey: "F minor",
        camelotKey: "4A",
        bpm: 122,
        platform: "bandcamp",
        externalUrl: "https://bandcamp.com/search?q=Talia%20North%20Amber%20Decaf",
        previewUrl: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3",
        durationSeconds: 384,
        note: "Dusty Rhodes lick tucked under a patient kick.",
        tags: ["deep-house", "keys", "warm-up"],
        playback: {
          availability: "preview",
          label: "Preview",
        },
      },
      {
        id: "track-sidechain-sunbeam",
        title: "Sidechain Sunbeam",
        artist: "Rivo & Melle",
        musicalKey: "A-flat major",
        camelotKey: "4B",
        bpm: 123,
        platform: "soundcloud",
        externalUrl:
          "https://soundcloud.com/search/sounds?q=Rivo%20Melle%20Sidechain%20Sunbeam",
        durationSeconds: 331,
        note: "Tiny vocal flecks, big couch-on-the-dancefloor energy.",
        tags: ["house", "vocal-chop", "sunny"],
        playback: {
          availability: "external-only",
          label: "Open on SoundCloud",
          reason: "No inline preview available yet.",
        },
      },
      {
        id: "track-linen-bassline",
        title: "Linen Bassline",
        artist: "Mira Solis",
        musicalKey: "B-flat minor",
        camelotKey: "3A",
        bpm: 124,
        platform: "beatport",
        externalUrl:
          "https://www.beatport.com/search?q=Mira%20Solis%20Linen%20Bassline",
        previewUrl: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3",
        durationSeconds: 407,
        note: "Moves like a small room opening its blinds.",
        tags: ["deep-house", "rolling", "bassline"],
        playback: {
          availability: "preview",
          label: "Preview",
        },
      },
    ],
  },
  {
    id: "crate-dusty-floor-starters",
    slug: "dusty-floor-starters",
    sectionId: "2026-04-27-morning-afterglow",
    source: "staff-pick",
    title: "Dusty Floor Starters",
    subtitle: "First three records after doors",
    note: "Slow-burn openers for when the room is still mostly friends, staff, and one person already dancing.",
    publishedAt: "2026-04-27T11:35:00-06:00",
    curator: {
      id: "mara-sb",
      name: "Mara",
      role: "Spinnin' Beans editor",
      handle: "@mara.digs",
    },
    artwork: {
      style: "riso-sleeve",
      alt: "Riso-style record sleeve with teal ink and hand-scrawled track notes.",
      palette: {
        background: "#d7efe8",
        foreground: "#16221f",
        accent: "#d04d3f",
      },
    },
    tags: ["staff-pick", "warm-up", "crate-digging", "house"],
    tracks: [
      {
        id: "track-basement-ferns",
        title: "Basement Ferns",
        artist: "Low Tide Social",
        musicalKey: "D minor",
        camelotKey: "7A",
        bpm: 119,
        platform: "bandcamp",
        externalUrl:
          "https://bandcamp.com/search?q=Low%20Tide%20Social%20Basement%20Ferns",
        previewUrl: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-4.mp3",
        durationSeconds: 350,
        note: "A little dusty, a little humid.",
        tags: ["deep-house", "opener", "organic"],
        playback: {
          availability: "preview",
          label: "Preview",
        },
      },
      {
        id: "track-flyer-glue",
        title: "Flyer Glue",
        artist: "Poster Run",
        musicalKey: "E minor",
        camelotKey: "9A",
        bpm: 121,
        platform: "soundcloud",
        externalUrl:
          "https://soundcloud.com/search/sounds?q=Poster%20Run%20Flyer%20Glue",
        durationSeconds: 312,
        note: "Scrappy drums with a tape-hiss hat loop.",
        tags: ["lo-fi-house", "dusty", "percussion"],
        playback: {
          availability: "external-only",
          label: "Open on SoundCloud",
        },
      },
      {
        id: "track-coat-check-loop",
        title: "Coat Check Loop",
        artist: "Velvet Janitor",
        musicalKey: "F-sharp minor",
        camelotKey: "11A",
        bpm: 120,
        platform: "beatport",
        externalUrl:
          "https://www.beatport.com/search?q=Velvet%20Janitor%20Coat%20Check%20Loop",
        previewUrl: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-5.mp3",
        durationSeconds: 397,
        note: "Patient, functional, quietly sticky.",
        tags: ["tool", "deep-house", "opening"],
        playback: {
          availability: "preview",
          label: "Preview",
        },
      },
    ],
  },
  {
    id: "crate-late-checkout-percussion",
    slug: "late-checkout-percussion",
    sectionId: "2026-04-26-warehouse-notes",
    source: "staff-pick",
    title: "Late Checkout Percussion",
    subtitle: "Tougher drums without the glare",
    note: "A compact stack for the point where the room wants motion but not full peak-time drama.",
    publishedAt: "2026-04-26T20:20:00-06:00",
    curator: {
      id: "niko-sb",
      name: "Niko",
      role: "Set Radar listener",
      handle: "@nikoinrotation",
    },
    artwork: {
      style: "record-label",
      alt: "Scuffed white-label record with orange marker dots.",
      palette: {
        background: "#f0eee5",
        foreground: "#1b1b1b",
        accent: "#e46f35",
      },
    },
    tags: ["percussive-house", "leftfield-club", "tools"],
    tracks: [
      {
        id: "track-copper-cowbell",
        title: "Copper Cowbell",
        artist: "Rhea Circuit",
        musicalKey: "G minor",
        camelotKey: "6A",
        bpm: 127,
        platform: "bandcamp",
        externalUrl:
          "https://bandcamp.com/search?q=Rhea%20Circuit%20Copper%20Cowbell",
        previewUrl: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-6.mp3",
        durationSeconds: 336,
        note: "A dry knock and enough low-end patience.",
        tags: ["percussion", "tools", "warehouse"],
        playback: {
          availability: "preview",
          label: "Preview",
        },
      },
      {
        id: "track-late-checkout",
        title: "Late Checkout Dub",
        artist: "Lobby Pressure",
        musicalKey: "C minor",
        camelotKey: "5A",
        bpm: 128,
        platform: "beatport",
        externalUrl:
          "https://www.beatport.com/search?q=Lobby%20Pressure%20Late%20Checkout%20Dub",
        durationSeconds: 401,
        note: "Drums first, synths barely above the fog.",
        tags: ["club", "dub", "percussion"],
        playback: {
          availability: "external-only",
          label: "Open on Beatport",
        },
      },
    ],
  },
  {
    id: "crate-friends-table",
    slug: "friends-table",
    sectionId: "2026-04-25-guest-window",
    source: "guest",
    title: "Friends Table",
    subtitle: "Small stacks from the side room",
    note: "Guest picks with a soft left turn: club records, tender edits, and a few odd sparks.",
    publishedAt: "2026-04-25T18:05:00-06:00",
    curator: {
      id: "guest-window",
      name: "Ari & Len",
      role: "Guest selectors",
      handle: "@friendstable",
    },
    artwork: {
      style: "show-photo",
      alt: "A cropped zine-style show photo with taped handwritten notes.",
      url: "/assets/discover.jpg",
      palette: {
        background: "#f3c3d7",
        foreground: "#221820",
        accent: "#f7cf46",
      },
    },
    tags: ["guest", "leftfield", "edits", "weekend"],
    tracks: [
      {
        id: "track-soft-warehouse-edit",
        title: "Soft Warehouse Edit",
        artist: "Pearl Scheduler",
        musicalKey: "A minor",
        camelotKey: "8A",
        bpm: 126,
        platform: "soundcloud",
        externalUrl:
          "https://soundcloud.com/search/sounds?q=Pearl%20Scheduler%20Soft%20Warehouse%20Edit",
        previewUrl: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-7.mp3",
        durationSeconds: 297,
        note: "A bootleg-shaped grin without losing the groove.",
        tags: ["edit", "club", "leftfield"],
        playback: {
          availability: "preview",
          label: "Preview",
        },
      },
      {
        id: "track-cafe-counter",
        title: "Cafe Counter",
        artist: "Sunday Caller",
        musicalKey: "D major",
        camelotKey: "10B",
        bpm: 118,
        platform: "youtube",
        externalUrl:
          "https://www.youtube.com/results?search_query=Sunday%20Caller%20Cafe%20Counter",
        durationSeconds: 275,
        note: "The cooldown track that still taps a glass.",
        tags: ["cooldown", "house", "soft-focus"],
        playback: {
          availability: "external-only",
          label: "Open on YouTube",
        },
      },
    ],
  },
  {
    id: "crate-community-clips",
    slug: "community-clips",
    sectionId: "2026-04-25-guest-window",
    source: "user-submission",
    title: "Community Clips",
    subtitle: "Signals from the inbox",
    note: "A tiny handful of submitted tracks that caught the room at the right angle.",
    publishedAt: "2026-04-25T14:10:00-06:00",
    curator: {
      id: "community",
      name: "The Inbox",
      role: "User submissions",
    },
    artwork: {
      style: "video-still",
      alt: "A collage-like video still with bright sticker shapes.",
      palette: {
        background: "#c7b7ff",
        foreground: "#17130f",
        accent: "#79c7b2",
      },
    },
    tags: ["user-submission", "clips", "new-finds"],
    tracks: [
      {
        id: "track-small-room-signal",
        title: "Small Room Signal",
        artist: "Nia Shelf",
        musicalKey: "E minor",
        camelotKey: "9A",
        bpm: 125,
        platform: "direct",
        externalUrl: "https://www.google.com/search?q=Nia%20Shelf%20Small%20Room%20Signal",
        previewUrl: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-8.mp3",
        durationSeconds: 302,
        note: "Bedroom mixdown, heavyweight idea.",
        tags: ["submission", "deep-house", "demo"],
        playback: {
          availability: "preview",
          label: "Preview",
        },
      },
      {
        id: "track-warm-index",
        title: "Warm Index",
        artist: "Patch Memory",
        musicalKey: "B minor",
        camelotKey: "10A",
        bpm: 123,
        platform: "bandcamp",
        externalUrl:
          "https://bandcamp.com/search?q=Patch%20Memory%20Warm%20Index",
        durationSeconds: 319,
        note: "A fuzzy little loop that refuses to leave.",
        tags: ["submission", "warm-up", "lo-fi"],
        playback: {
          availability: "external-only",
          label: "Open on Bandcamp",
        },
      },
    ],
  },
];

export async function getMiniCrates(options = {}) {
  try {
    return await loadLiveMiniCrateResult(options);
  } catch (error) {
    return buildFixtureMiniCrateResult(options);
  }
}

function buildFixtureMiniCrateResult(options) {
  const filteredCrates = filterCrates(miniCrateFixtures, options);
  const activeSectionIds = new Set(filteredCrates.map((crate) => crate.sectionId));
  const sections = miniCrateSections
    .filter((section) => activeSectionIds.has(section.id))
    .sort((first, second) => first.sortOrder - second.sortOrder);
  const tracksPerCrate = normalizeTracksPerCrate(
    options.tracks_per_crate ?? options.tracksPerCrate,
    DEFAULT_TRACKS_PER_CRATE
  );

  return {
    crates: filteredCrates.map((crate) => cloneMiniCrate(crate, tracksPerCrate)),
    sections: sections.map((section) => ({ ...section })),
    sourceOptions: discoverSourceOptions.map((sourceOption) => ({ ...sourceOption })),
    filters: normalizeFilters(options),
    total: filteredCrates.length,
    generatedAt: new Date().toISOString(),
    source: "fixture",
  };
}

async function loadLiveMiniCrateResult(options) {
  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timeout = controller
    ? setTimeout(() => controller.abort(), 8000)
    : null;

  try {
    const response = await fetch(
      buildMiniCratesUrl(options),
      controller ? { signal: controller.signal } : undefined
    );

    if (!response.ok) {
      throw new Error(`Discover mini-crates failed with ${response.status}`);
    }

    return normalizeLiveMiniCrateResponse(await response.json(), options);
  } finally {
    if (timeout) {
      clearTimeout(timeout);
    }
  }
}

function buildMiniCratesUrl(options) {
  const url = new URL("/api/discover/mini-crates", API_BASE);
  const sources = normalizeSources(options.sources);
  const tracksPerCrate = normalizeTracksPerCrate(
    options.tracks_per_crate ?? options.tracksPerCrate,
    null
  );

  sources.forEach((source) => {
    url.searchParams.append("sources", source);
  });

  if (tracksPerCrate) {
    url.searchParams.set("tracks_per_crate", String(tracksPerCrate));
  }

  return url.toString();
}

function normalizeLiveMiniCrateResponse(payload, options) {
  if (!payload || !Array.isArray(payload.crates) || !Array.isArray(payload.sections)) {
    throw new Error("Discover mini-crates returned an invalid payload");
  }

  const crates = payload.crates
    .map(normalizeMiniCrate)
    .filter(Boolean);
  const activeSectionIds = new Set(crates.map((crate) => crate.sectionId));
  const sections = payload.sections
    .map(normalizeMiniCrateSection)
    .filter((section) => section && activeSectionIds.has(section.id))
    .sort((first, second) => first.sortOrder - second.sortOrder);
  const sourceOptions = Array.isArray(payload.sourceOptions)
    ? payload.sourceOptions.map(normalizeSourceOption).filter(Boolean)
    : [];

  return {
    crates,
    sections,
    sourceOptions: sourceOptions.length > 0
      ? sourceOptions
      : discoverSourceOptions.map((sourceOption) => ({ ...sourceOption })),
    filters: payload.filters || normalizeFilters(options),
    total: readNumber(payload, "total") ?? crates.length,
    generatedAt: readString(payload, "generatedAt") || new Date().toISOString(),
    source: "live",
  };
}

function normalizeMiniCrate(crate) {
  const id = readString(crate, "id");
  const title = readString(crate, "title");
  const source = normalizeSource(crate?.source);
  const tracks = Array.isArray(crate?.tracks)
    ? crate.tracks.map(normalizeMiniCrateTrack).filter(Boolean)
    : [];

  if (!id || !title || !source) {
    return null;
  }

  return {
    id,
    slug: readString(crate, "slug") || id,
    sectionId: readString(crate, "sectionId") || "live",
    source,
    title,
    subtitle: readNullableString(crate, "subtitle"),
    note: readString(crate, "note") || "",
    publishedAt: readString(crate, "publishedAt") || new Date().toISOString(),
    curator: normalizeCurator(crate.curator),
    artwork: normalizeArtwork(crate.artwork, title),
    tags: normalizeStringArray(crate.tags),
    tracks,
    stats: crate.stats || undefined,
  };
}

function normalizeMiniCrateTrack(track) {
  const id = readString(track, "id");
  const title = readString(track, "title");
  const artist = readString(track, "artist");

  if (!id || !title || !artist) {
    return null;
  }

  const previewUrl = sanitizeAssetUrl(readNullableString(track, "previewUrl"));
  const previewEndpoint = sanitizeAssetUrl(readNullableString(track, "previewEndpoint"));
  const platform = normalizePlatform(track.platform);

  return {
    id,
    title,
    artist,
    musicalKey: readNullableString(track, "musicalKey"),
    camelotKey: readNullableString(track, "camelotKey"),
    bpm: readNumber(track, "bpm"),
    platform,
    externalUrl: readString(track, "externalUrl") || "",
    previewUrl,
    previewEndpoint,
    artworkUrl: sanitizeAssetUrl(readNullableString(track, "artworkUrl")),
    durationSeconds: readNumber(track, "durationSeconds"),
    note: readNullableString(track, "note"),
    tags: normalizeStringArray(track.tags),
    playback: normalizePlayback(track.playback, platform, previewUrl, previewEndpoint),
    stats: track.stats || undefined,
  };
}

function normalizeMiniCrateSection(section) {
  const id = readString(section, "id");

  if (!id) {
    return null;
  }

  return {
    id,
    date: readString(section, "date") || "",
    dayLabel: readString(section, "dayLabel") || "",
    title: readString(section, "title") || "Fresh Crates",
    theme: readString(section, "theme") || "",
    summary: readString(section, "summary") || "",
    sortOrder: readNumber(section, "sortOrder") ?? 0,
  };
}

function normalizeSourceOption(sourceOption) {
  const id = normalizeSource(sourceOption?.id);
  const fallback = discoverSourceOptions.find((option) => option.id === id);

  if (!id) {
    return null;
  }

  return {
    id,
    label: readString(sourceOption, "label") || fallback?.label || id,
    shortLabel: readString(sourceOption, "shortLabel") || fallback?.shortLabel || id,
    description: readString(sourceOption, "description") || fallback?.description || "",
  };
}

function normalizeCurator(curator) {
  return {
    id: readString(curator, "id") || "sb-vibe",
    name: readString(curator, "name") || "SB-VIBE",
    role: readString(curator, "role") || "Taste engine",
    handle: readNullableString(curator, "handle"),
    avatarUrl: sanitizeAssetUrl(readNullableString(curator, "avatarUrl")),
  };
}

function normalizeArtwork(artwork, title) {
  return {
    style: readString(artwork, "style") || "gradient-paper",
    alt: readString(artwork, "alt") || `${title} mini-crate artwork.`,
    url: sanitizeAssetUrl(readNullableString(artwork, "url")),
    palette: normalizePalette(artwork?.palette),
  };
}

function normalizePlayback(playback, platform, previewUrl, previewEndpoint) {
  const availability = playback?.availability;
  const hasPlayableSource = Boolean(previewUrl || previewEndpoint);

  if (availability === "preview" || availability === "external-only" || availability === "unavailable") {
    return {
      availability: hasPlayableSource ? "preview" : availability,
      label: readString(playback, "label") || (hasPlayableSource ? "Preview" : `Open on ${platform}`),
      reason: readNullableString(playback, "reason"),
    };
  }

  return hasPlayableSource
    ? {
        availability: "preview",
        label: "Preview",
      }
    : {
        availability: "external-only",
        label: `Open on ${platform}`,
        reason: "No inline preview available yet.",
      };
}

function filterCrates(crates, filters) {
  const sources = normalizeSources(filters.sources);

  return crates.filter((crate) => {
    if (sources.length && !sources.includes(crate.source)) {
      return false;
    }

    return true;
  });
}

function normalizeFilters(options) {
  const sources = normalizeSources(options.sources);

  return {
    sources: sources.length > 0 ? sources : undefined,
  };
}

function normalizeSources(sources) {
  if (!Array.isArray(sources)) {
    return [];
  }

  return sources.map(normalizeSource).filter(Boolean);
}

function normalizeSource(source) {
  if (
    source === "sb-vibe" ||
    source === "staff-pick" ||
    source === "guest" ||
    source === "user-submission"
  ) {
    return source;
  }

  return null;
}

function normalizeTracksPerCrate(value, fallback) {
  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return fallback;
  }

  return Math.max(1, Math.min(12, Math.floor(numberValue)));
}

function normalizeStringArray(value) {
  return Array.isArray(value)
    ? value.filter((item) => typeof item === "string" && item.trim().length > 0)
    : [];
}

function normalizePalette(palette) {
  const background = readString(palette, "background");
  const foreground = readString(palette, "foreground");
  const accent = readString(palette, "accent");

  if (!background || !foreground || !accent) {
    return undefined;
  }

  return {
    background,
    foreground,
    accent,
  };
}

function sanitizeAssetUrl(url) {
  if (!url || url === "/placeholder.jpg" || url.endsWith("/placeholder.jpg")) {
    return undefined;
  }

  return url;
}

function readString(source, key) {
  const value = source?.[key];
  return typeof value === "string" && value.trim().length > 0 ? value : undefined;
}

function readNullableString(source, key) {
  return readString(source, key) || undefined;
}

function readNumber(source, key) {
  const value = source?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function normalizePlatform(platform) {
  if (
    platform === "beatport" ||
    platform === "soundcloud" ||
    platform === "bandcamp" ||
    platform === "youtube" ||
    platform === "spotify"
  ) {
    return platform;
  }

  return "direct";
}

function cloneMiniCrate(crate, tracksPerCrate = DEFAULT_TRACKS_PER_CRATE) {
  return {
    ...crate,
    curator: {
      ...crate.curator,
    },
    artwork: {
      ...crate.artwork,
      palette: crate.artwork.palette
        ? {
            ...crate.artwork.palette,
          }
        : undefined,
    },
    tags: [...crate.tags],
    tracks: crate.tracks.slice(0, tracksPerCrate).map((track) => ({
      ...track,
      tags: [...track.tags],
      playback: {
        ...track.playback,
      },
    })),
  };
}

export { API_BASE };
